/**
 * Retrieves a value from the model defined by path string.
 *
 * @param model {object}
 * @param path {string} Dot-separated path within the model
 * @apram defaultValue {object} Optional default value if path's value is undefined
 * @returns {*}
 */
export const getValueFromPath = (model, path = '', defaultValue = undefined) => {
    const components = path.split('.');
    const value = components.reduce(
        (previous, current) =>
            previous === undefined || previous === null ? undefined : previous[current],
        model
    );
    if (value === undefined && defaultValue !== undefined) {
        return defaultValue;
    }
    return value;
};

/**
 * Assigns a value in the model at defined path.
 *
 * @param model {object}
 * @param path {string} Dot-separated path within the model
 * @param value
 */
export const setValueAtPath = (model, path, value) => {
    const components = path.split('.');
    const field = components.slice(0, components.length - 1).reduce((previous, current) => {
        const acc = previous;
        if (acc[current] === undefined || acc[current] === null) {
            acc[current] = {};
        }
        return acc[current];
    }, model);
    field[components[components.length - 1]] = value;
};

/**
 * Why this exists if there is native function? Because some pages, e.g. CSOB has overridden it
 * and it parses the word as regex and it fails. So in such situations we use this method instead of native.
 *
 * @param string
 * @param word
 * @returns {*|boolean}
 */
export const startsWith = (string, word) =>
    string && string.length >= word.length && string.substring(0, word.length) === word;

/**
 * Returns object with keys representing groups, values with arrays grouped by groupFn key
 *
 * @param groupFn Function that returns group key, input parameter is an item of the array
 */
export const groupBy = (groupFn) => (array = []) =>
    array.reduce((result, item) => {
        const key = groupFn(item);
        return {
            ...result,
            [key]: (result[key] || []).push(item),
        };
    }, {});

export const unnest = (array) =>
    array.reduce((result, item) => (Array.isArray(item) ? [...result, ...item] : [...result, item]), []);

const getValueFromPathDeepParse = (model, path = '', defaultValue = undefined) => {
    const components = path.split('.');
    const candidates = [];
    const value = components.reduce((previous, current, index) => {
        if (previous === undefined || previous === null) {
            return undefined;
        }
        const next = previous[current];
        if (typeof next === 'string' && index < components.length - 1) {
            try {
                const parsed = JSON.parse(next);
                candidates.push([parsed, components.slice(index + 1).join('.')]);
            } catch (e) {
                return next;
            }
        }

        return next;
    }, model);

    return (
        value ||
        candidates
            .map((candidateArgs) => getValueFromPathDeepParse(...candidateArgs))
            .find((candidate) => candidate !== undefined) ||
        defaultValue
    );
};

/**
 * Tries to retrieve value defined by path via retrievalFunction. If full path is not matched, it cuts right-most part separated by a dot. If found, value is retrieved by the cut dotted part.
 *
 * Example: path = yeself-console.security.token
 * State: we have "yeself-console" key in local storage and it contains JSON string value under "security.token"
 *
 * @param {*} path
 * @param {*} retrievalFunction
 */
export const iteratePathDefinitionAndGetValue = (path = '', retrievalFunction) => {
    if (!path) return undefined;
    const pathParts = path.split('.');
    const valueInsidePath = pathParts.reduceRight(
        (acc, pathPart) => {
            const retrievedResult =
                acc.keyPaths && acc.keyPaths.length && retrievalFunction(acc.keyPaths.join('.'));
            const parsedResult =
                acc.valuePaths && acc.valuePaths.length
                    ? getValueFromPathDeepParse(JSON.parse(retrievedResult), acc.valuePaths.join('.'))
                    : retrievedResult;
            const value = parsedResult || acc.value;
            return value
                ? { value }
                : { keyPaths: acc.keyPaths.slice(0, -1), valuePaths: [pathPart, ...acc.valuePaths] };
        },
        { keyPaths: pathParts, valuePaths: [] }
    );
    return valueInsidePath.value;
};

export const queryParams = (valuesObject) =>
    Object.keys(valuesObject).reduce(
        (acc, name, index) =>
            `${acc}${index > 0 ? '&' : ''}${name}=${encodeURIComponent(valuesObject[name])}`,
        ''
    );

export const sort = (comparator) => (arr) => [...arr].sort(comparator);

export const isValidRegExp = (regexp) => {
    try {
        const valid = new RegExp(regexp);
        return !!valid;
    } catch (err) {
        return false;
    }
};

export const getParameterByName = (name) => {
    const match = RegExp(`[?&]${name}=([^&]*)`).exec(window.location.search);
    return (match?.length && decodeURIComponent(match[1].replace(/\+/g, ' '))) || '';
};
